import type { ArcadeGameKind } from "@/utils/arcade/games";
import { createBassBlaster } from "./bassBlaster";
import { createBeatLanes } from "./beatLanes";
import { createNightDrive, hasDriveView, registerDriveView } from "./nightDrive";
import { createPulsePads } from "./pulsePads";
import type { GameEngine, GameEnv } from "./types";
import { createVineRunner } from "./vineRunner";
import { createVinylOrbit } from "./vinylOrbit";

export type { FrameInput, GameEngine, GameEnv, GameStatus } from "./types";
export { EMPTY_INPUT } from "./types";
export { createArtKit, createPostFx, defaultCanvasFactory, noCanvas, type ArtKit, type PostFx } from "./render";

const FACTORIES: Record<ArcadeGameKind, (env: GameEnv) => GameEngine> = {
  "vine-runner": createVineRunner,
  "beat-lanes": createBeatLanes,
  "bass-blaster": createBassBlaster,
  "vinyl-orbit": createVinylOrbit,
  "night-drive": createNightDrive,
  "pulse-pads": createPulsePads,
};

const hasWebGL = () => {
  try {
    const canvas = typeof document !== "undefined" ? document.createElement("canvas") : typeof OffscreenCanvas !== "undefined" ? new OffscreenCanvas(1, 1) : null;
    return Boolean(canvas && (canvas.getContext("webgl2") || canvas.getContext("webgl")));
  } catch {
    return false;
  }
};

let driveViewLoad: Promise<void> | null = null;

// Loads whatever a game needs beyond the core bundle. Night Drive pulls in
// three.js here, and only when WebGL works; otherwise it keeps its 2D road.
export function prepareGame(kind: ArcadeGameKind): Promise<void> {
  if (kind !== "night-drive" || hasDriveView()) return Promise.resolve();
  if (!driveViewLoad) {
    driveViewLoad = hasWebGL()
      ? import("./nightDrive3d").then((module) => registerDriveView(module.createDriveView3D)).catch(() => undefined)
      : Promise.resolve();
  }
  return driveViewLoad;
}

export function createGame(kind: ArcadeGameKind, env: GameEnv): GameEngine {
  return FACTORIES[kind](env);
}
